import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Orb } from '@sorare/core/src/components/Orb';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { ensureHexColor } from '@sorare/core/src/lib/colors.ts';

import { RevealPackCardsDialogFromReward } from '#src/components/commonCard/RevealPackCardsDialogFromReward/index.tsx';
import { useSeriesDailyClaim } from '#src/hooks/packs/useSeriesDailyClaim.ts';

import type {
  SeriesDailyClaimModule_cardSet,
  SeriesDailyClaimModule_task,
} from './__generated__/index.graphql.ts';

const PackImage = styled.img`
  height: 40px;
  object-fit: contain;
  transition: opacity 0.3s ease-in-out;
  .loading & {
    opacity: 0.6;
  }
`;

type Props = {
  cardSet?: Nullable<SeriesDailyClaimModule_cardSet>;
  task?: SeriesDailyClaimModule_task;
  refetch: () => Promise<unknown>;
  onClaimed?: () => void;
};

export const SeriesDailyClaimModule = ({
  cardSet,
  task,
  refetch,
  onClaimed,
}: Props) => {
  const mainColor = cardSet?.branding?.colors?.main;
  const orbColor = ensureHexColor(mainColor);
  const {
    loading,
    isClaimable,
    pack,
    onClaim,
    reward,
    packDialogOpen,
    setPackDialogOpen,
    claimMutationData,
    dailyTasksExpiresAt,
  } = useSeriesDailyClaim(task, onClaimed);

  return (
    <>
      <Orb
        showNotificationDot={true}
        onClick={onClaim}
        disabled={!isClaimable}
        loading={loading}
        color={orbColor}
        chipState={isClaimable ? 'reward' : 'timeLeft'}
        label={
          isClaimable
            ? null
            : dailyTasksExpiresAt && (
                <TimeLeft time={dailyTasksExpiresAt} onEnded={refetch} />
              )
        }
      >
        {!!pack?.pictureUrl && <PackImage src={pack.pictureUrl} alt="" />}
      </Orb>
      {!!reward && (
        <RevealPackCardsDialogFromReward
          open={packDialogOpen}
          reward={claimMutationData?.rewards?.at(0)}
          onClose={() => {
            setPackDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

SeriesDailyClaimModule.fragments = {
  cardSet: gql`
    fragment SeriesDailyClaimModule_cardSet on CardSet {
      slug
      branding {
        id
        colors {
          main
        }
      }
    }
  ` as TypedDocumentNode<SeriesDailyClaimModule_cardSet>,
  task: gql`
    fragment SeriesDailyClaimModule_task on Task {
      id
      ...useSeriesDailyClaim_task
    }
    ${useSeriesDailyClaim.fragments.task}
  ` as TypedDocumentNode<SeriesDailyClaimModule_task>,
};
